/**
 * Next action handlers for processor-specific user actions (e.g., 3DS authentication).
 *
 * This module abstracts processor-specific logic away from the generic card payment flow.
 * Each handler implements the logic needed for a specific next action type.
 */

import type { PublicCardCheckoutResponse } from "@pkg/sdk/models";
import type { Stripe } from "@stripe/stripe-js";
import show3DSIframeModal, {
  MODAL_CLOSE_BUTTON_SHADOW,
  MODAL_OVERLAY_SCRIM,
  MODAL_SHADOW,
} from "../utils/3ds-iframe-modal";
import { handleBraintree3ds } from "./braintree-3ds";
import { isStripeJsAvailable } from "./stripe-js-loader";

// Shared 3DS modal width for show3DSIframeModal callers and the Adyen native
// container. The native container needs it explicitly, or the centering flex
// collapses it to the close button's min-content width.
const MODAL_WIDTH_PX = 605;

// Helper to wait for a condition
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Get the Stripe instance from the global window object.
 * Expects Stripe.js to be loaded via HTML script tag.
 * Polls for up to 5 seconds waiting for Stripe to be available.
 */
export const getLoadedStripe = async (publishableKey: string): Promise<Stripe> => {
  // Poll for Stripe.js to be loaded (up to 5 seconds)
  for (let i = 0; i < 10; i++) {
    if (isStripeJsAvailable()) {
      break;
    }
    await sleep(500);
  }

  if (!isStripeJsAvailable()) {
    throw new Error(
      "Stripe.js not loaded. Add this script tag to your HTML <head>:\n" +
        '<script src="https://js.stripe.com/v3/"></script>',
    );
  }

  // @ts-expect-error Stripe is loaded globally via script tag
  const stripe: Stripe = new window.Stripe(publishableKey);
  return stripe;
};

// ---------------------------------------------------------------------------
// Adyen native 3DS2 (fingerprint + challenge)
// ---------------------------------------------------------------------------
//
// WHY the Adyen Web SDK and NOT the shared show3DSIframeModal (VGS/Airwallex
// precedent): native Adyen 3DS2 does not hand us a ready-to-render challenge form
// or a redirect URL that posts back to OUR 3ds-callback. It returns an OPAQUE
// `action` whose base64 `token` drives the EMVCo device-fingerprint / CReq-CRes
// challenge handshake against the ISSUER's ACS, with the result posted back to
// ADYEN's notification URL — a protocol the generic modal cannot speak. The card
// loop needs the component's `state.data` (`{ details, paymentData }`) to submit to
// `/payments/details`; the modal only ever signals "closed", never that payload. So
// per the plan's escape hatch we use Adyen Web's `createFromAction`, which
// encapsulates the handshake and yields `state.data` via `onAdditionalDetails`.
//
// Loaded GLOBALLY from Adyen's CDN (like Stripe.js — `window.AdyenCheckout`), NOT
// bundled: no new package.json / bundle-size commitment, and the dep-vs-bundle call
// stays Augustin's (CLAUDE.md: frontend + dependency approval). The merchant adds
// the Adyen Web script + CSS to their <head>, exactly as they already do for
// Stripe.js.

/** Minimal shape of the globally-loaded Adyen Web component we drive. */
type AdyenActionComponent = { mount: (container: HTMLElement | string) => unknown; unmount?: () => void };

/** Minimal shape of an `AdyenCheckout` core instance (only what we call). */
type AdyenCheckoutInstance = {
  createFromAction: (action: Record<string, unknown>, options?: Record<string, unknown>) => AdyenActionComponent;
};

/** `state.data` yielded by the 3DS2 components — the verify body we round-trip. */
type AdyenAdditionalDetailsState = { data?: { details?: Record<string, unknown>; paymentData?: string } };

type AdyenCheckoutConfig = {
  clientKey: string;
  environment: string;
  onAdditionalDetails?: (state: AdyenAdditionalDetailsState) => void;
  onError?: (error: unknown) => void;
};

/** The global factory the Adyen Web CDN build installs (async in v5/v6). */
type AdyenCheckoutFactory = (config: AdyenCheckoutConfig) => Promise<AdyenCheckoutInstance>;

const isAdyenCheckoutPresent = (): boolean => "AdyenCheckout" in window;

/**
 * Get the Adyen Web factory from the global window object (loaded via CDN script
 * tag, mirroring `getLoadedStripe`). Polls for up to 5 seconds.
 */
const getLoadedAdyen = async (): Promise<AdyenCheckoutFactory> => {
  for (let i = 0; i < 10; i++) {
    if (isAdyenCheckoutPresent()) {
      break;
    }
    await sleep(500);
  }

  if (!isAdyenCheckoutPresent()) {
    throw new Error(
      "Adyen Web not loaded. Native 3DS2 needs the Adyen Web v5 UMD SDK — this " +
        "handler drives the v5 `window.AdyenCheckout` async factory (v6 is ESM " +
        "and exposes a different API). Add these to your HTML <head> " +
        "(swap `-test` for `-live` in production):\n" +
        '<script src="https://checkoutshopper-test.adyen.com/checkoutshopper/sdk/5.72.0/adyen.js"></script>\n' +
        '<link rel="stylesheet" href="https://checkoutshopper-test.adyen.com/checkoutshopper/sdk/5.72.0/adyen.css" />',
    );
  }

  // @ts-expect-error AdyenCheckout is loaded globally via script tag
  return window.AdyenCheckout as AdyenCheckoutFactory;
};

/**
 * Result of handling a next action.
 *
 * `data` mirrors the shared 3DS iframe-modal result (`Record<string, unknown>`). For
 * native 3DS2 (Adyen) it carries the component's `state.data` (`{ details, paymentData }`),
 * which the card loop narrows and forwards to `cardCheckoutVerify` as the verify body.
 * Redirect / VGS flows produce no meaningful data (their result reaches the backend via
 * the ACS callback + server poll).
 */
export type NextActionResult = { success: true; data?: Record<string, unknown> } | { success: false; error: string };

/**
 * Handle a Stripe SetupIntent 3DS authentication (card-save / RBI e-mandate registration).
 *
 * A DISTINCT next_action from `stripe_3ds`: it drives the challenge with `confirmCardSetup`
 * because the client secret belongs to a SetupIntent, not a PaymentIntent. The India e-mandate
 * registration AFA flows through here — Stripe returns `requires_action` on the mandate
 * SetupIntent, the backend surfaces it as a `stripe_setup_3ds` action carrying the
 * `seti_..._secret_...` secret, and completing this challenge lets the checkout's setup-verify
 * finalize the registration and proceed to the first on-session charge. Routed by the
 * `stripe_setup_3ds` discriminator (see `handleNextAction`), never by inspecting the prefix.
 */
const handleStripeSetup3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "stripe_setup_3ds" }>,
): Promise<NextActionResult> => {
  const { clientSecret, stripePk } = nextAction;

  // Defense in depth: a stripe_setup_3ds MUST carry a SetupIntent secret. Fail loudly on a
  // mismatch rather than silently running confirmCardSetup against the wrong object.
  if (!clientSecret.startsWith("seti_")) {
    console.error("[3DS:Stripe:Setup] Expected a SetupIntent (seti_) client secret; got:", clientSecret.slice(0, 8));
    return { success: false, error: "Unexpected client secret for a SetupIntent 3DS action." };
  }

  const stripe = await getLoadedStripe(stripePk);

  // Show 3DS modal (SetupIntent variant)
  const { error: stripeError } = await stripe.confirmCardSetup(clientSecret);

  if (stripeError) {
    console.error("[3DS:Stripe:Setup] Authentication failed:", stripeError.message);
    return { success: false, error: stripeError.message || "3DS authentication failed" };
  }

  return { success: true };
};

/**
 * Handle Stripe PaymentIntent 3DS authentication.
 *
 * The PAYMENT-time 3DS: the client secret is a PaymentIntent secret (`pi_..._secret_...`),
 * completed with `confirmCardPayment`. Routing is by the `stripe_3ds` discriminator (see
 * `handleNextAction`), NOT by inspecting the secret's prefix — the SetupIntent AFA is the
 * distinct `stripe_setup_3ds` action handled by `handleStripeSetup3ds`.
 */
const handleStripe3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "stripe_3ds" }>,
): Promise<NextActionResult> => {
  const { clientSecret, stripePk } = nextAction;

  // Defense in depth: a stripe_3ds MUST carry a PaymentIntent secret. Fail loudly on a mismatch
  // rather than silently running confirmCardPayment against a SetupIntent secret.
  if (!clientSecret.startsWith("pi_")) {
    console.error("[3DS:Stripe] Expected a PaymentIntent (pi_) client secret; got:", clientSecret.slice(0, 8));
    return { success: false, error: "Unexpected client secret for a PaymentIntent 3DS action." };
  }

  const stripe = await getLoadedStripe(stripePk);

  // Show 3DS modal
  const { error: stripeError } = await stripe.confirmCardPayment(clientSecret);

  if (stripeError) {
    console.error("[3DS:Stripe] Authentication failed:", stripeError.message);
    return { success: false, error: stripeError.message || "3DS authentication failed" };
  }

  return { success: true };
};

/**
 * Handle Airwallex 3DS authentication via iframe.
 *
 * Renders an iframe with the 3DS authentication URL. The iframe will redirect
 * to the 3DS callback page which sends a postMessage back to complete the flow.
 */
const handleAirwallex3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "airwallex_3ds" }>,
): Promise<NextActionResult> => {
  return show3DSIframeModal({
    url: nextAction.url,
    size: { width: MODAL_WIDTH_PX, height: 550 },
    onMessage: (event) => {
      if (event.data.type !== "3ds_complete") {
        return { handled: false };
      }

      const { isSuccess, error } = event.data;

      if (isSuccess) {
        return { handled: true, result: { success: true, data: {} } };
      } else {
        console.error("[3DS:Airwallex] Authentication failed:", error);
        return {
          handled: true,
          result: { success: false, error: error || "3DS authentication failed" },
        };
      }
    },
  });
};

/**
 * Checkout.com 3DS handler (redirect / iframe).
 *
 * Identical flow to Airwallex: render an iframe with Checkout.com's hosted-3DS
 * URL. After the shopper authenticates, Checkout.com redirects to the return page
 * which postMessages `3ds_complete` back; the charge is confirmed server-side by
 * `verify_user_action` (GET /payments/{id}) — no client_secret, no client SDK.
 */
const handleCheckoutcom3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "checkoutcom_3ds" }>,
): Promise<NextActionResult> => {
  return show3DSIframeModal({
    url: nextAction.url,
    size: { width: MODAL_WIDTH_PX, height: 550 },
    onMessage: (event) => {
      if (event.data.type !== "3ds_complete") {
        return { handled: false };
      }

      const { isSuccess, error } = event.data;

      if (isSuccess) {
        return { handled: true, result: { success: true, data: {} } };
      } else {
        console.error("[3DS:Checkout.com] Authentication failed:", error);
        return {
          handled: true,
          result: { success: false, error: error || "3DS authentication failed" },
        };
      }
    },
  });
};

/**
 * VonPay 3DS handler (embedded iframe 3DS challenge).
 *
 * Identical flow to Airwallex / Checkout.com: VonPay runs native 3DS as an EMBEDDED
 * IFRAME challenge (Q-VONPAY-32). The challenged charge returns `requires_action` with
 * `next_action.redirect_to_url.url` (VonPay's field name — the URL is iframed, not
 * navigated to), flattened onto `nextAction.url` by the backend `Vonpay3dsAction`. We
 * mount the challenge URL in an in-page iframe (`show3DSIframeModal`); the browser never
 * leaves the checkout page. After the shopper authenticates, VonPay redirects the iframe
 * to the return page which postMessages
 * `3ds_complete` back, and the charge is resolved server-side by `verify_user_action`
 * (confirm-by-fetch on the `vpi_` intent) — there is NO client_secret and no client
 * SDK.
 *
 * NOTE: FE changes require Augustin's approval before merge (pre-pilot handoff), and
 * the generated SDK must be regenerated (`make sdk`) so `PublicCardCheckoutResponse`
 * carries the `vonpay_3ds` member before this branch is type-reachable.
 */
const handleVonpay3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "vonpay_3ds" }>,
): Promise<NextActionResult> => {
  return show3DSIframeModal({
    url: nextAction.url,
    size: { width: MODAL_WIDTH_PX, height: 550 },
    onMessage: (event) => {
      if (event.data.type !== "3ds_complete") {
        return { handled: false };
      }

      const { isSuccess, error } = event.data;

      if (isSuccess) {
        return { handled: true, result: { success: true, data: {} } };
      } else {
        console.error("[3DS:VonPay] Authentication failed:", error);
        return {
          handled: true,
          result: { success: false, error: error || "3DS authentication failed" },
        };
      }
    },
  });
};

/**
 * Handle VGS 3DS authentication — challenge only.
 *
 * Device fingerprinting already ran proactively at card tokenization (a hidden
 * background iframe injected from the setup/tokenize response), so this next-action
 * is ONLY ever a step-up CHALLENGE: challengeForm and/or challengeUrl is present.
 * The backend is the source of truth for the true 3DS outcome — the loop's
 * cardCheckoutVerify() call (in card.ts) polls VGS and records it — so this handler
 * just renders the challenge and resolves once the modal closes.
 */
const handleVgs3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "vgs_3ds" }>,
): Promise<NextActionResult> => {
  const { challengeForm, challengeUrl } = nextAction;
  if (!challengeForm && !challengeUrl) {
    // Defensive: the backend only emits vgs_3ds for challenges.
    return { success: true };
  }
  // The callback page (3ds-callback.tsx) posts `3ds_complete` after the ACS redirect;
  // we only use it to close the modal, never its isSuccess — backend verify is truth.
  const modalResult = await show3DSIframeModal({
    srcdocHtml: challengeForm ?? undefined,
    url: challengeForm ? undefined : (challengeUrl ?? undefined),
    size: { width: MODAL_WIDTH_PX, height: 550 },
    onMessage: (event) => {
      if (event.data?.type !== "3ds_complete") {
        return { handled: false };
      }
      return { handled: true, result: { success: true, data: {} } };
    },
  });
  // success=true → `3ds_complete` arrived (challenge completed); success=false → user
  // dismissed / load error before completion. Surface the cancellation, don't fake it.
  if (!modalResult.success) {
    return { success: false, error: modalResult.error };
  }
  return { success: true };
};

/**
 * Handle the Adyen `redirect` action in the shared 3DS iframe (like Airwallex).
 * The ACS redirects to our 3ds-callback, which posts `3ds_complete`; the backend
 * verify is the source of truth, so the flag only closes the modal. Produces no
 * client `state.data` (the redirectResult reaches the backend via the callback).
 */
const handleAdyenRedirect = async (action: Record<string, unknown>): Promise<NextActionResult> => {
  const url = typeof action.url === "string" ? action.url : undefined;
  if (!url) {
    return {
      success: false,
      error: "Unsupported Adyen redirect action (no url).",
    };
  }
  return show3DSIframeModal({
    url,
    size: { width: MODAL_WIDTH_PX, height: 550 },
    onMessage: (event) => {
      if (event.data.type !== "3ds_complete") {
        return { handled: false };
      }
      const { isSuccess, error } = event.data;
      if (isSuccess) {
        return { handled: true, result: { success: true, data: {} } };
      }
      console.error("[3DS:Adyen] Redirect authentication failed:", error);
      return {
        handled: true,
        result: { success: false, error: error || "3DS authentication failed" },
      };
    },
  });
};

/**
 * Mount an Adyen challenge component inside a lightweight modal overlay and return
 * a teardown fn. Mirrors show3DSIframeModal's chrome (dimmed overlay, close button)
 * but hosts the Adyen component instead of our own iframe (the component renders the
 * issuer ACS iframe itself). `onCancel` fires if the shopper dismisses before the
 * component completes; the caller resolves the flow.
 */
const mountAdyenChallengeModal = (onCancel: () => void): { container: HTMLElement; teardown: () => void } => {
  const overlay = document.createElement("div");
  overlay.id = "pk-adyen-3ds-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "3D Secure Verification");
  overlay.style.cssText = `
    position: fixed; inset: 0; width: 100%; height: 100%;
    background: ${MODAL_OVERLAY_SCRIM};
    display: flex; align-items: center; justify-content: center; z-index: 10000;`;

  const container = document.createElement("div");
  container.style.cssText = `
    position: relative; background: white; border-radius: 6px; padding: 8px;
    width: ${MODAL_WIDTH_PX}px; max-width: calc(100vw - 32px); max-height: calc(100vh - 64px); overflow: auto;
    box-shadow: ${MODAL_SHADOW};`;

  const closeButton = document.createElement("button");
  closeButton.setAttribute("aria-label", "Cancel verification");
  closeButton.setAttribute("type", "button");
  closeButton.style.cssText = `
    position: absolute; top: 8px; right: 8px; width: 28px; height: 28px; border: none;
    background: white; border-radius: 8px; cursor: pointer; z-index: 10;
    box-shadow: ${MODAL_CLOSE_BUTTON_SHADOW}; font-size: 16px; line-height: 1;`;
  closeButton.textContent = "×";
  closeButton.onclick = onCancel;

  const mountPoint = document.createElement("div");

  container.appendChild(closeButton);
  container.appendChild(mountPoint);
  overlay.appendChild(container);
  document.body.appendChild(overlay);

  return {
    container: mountPoint,
    teardown: () => overlay.remove(),
  };
};

/**
 * Mount a hidden, off-screen container for the invisible device-fingerprint
 * component (identifyShopper). No visible UI; the caller removes it on completion.
 */
const mountAdyenHiddenContainer = (): { container: HTMLElement; teardown: () => void } => {
  const holder = document.createElement("div");
  holder.setAttribute("aria-hidden", "true");
  holder.style.cssText = "position: absolute; width: 0; height: 0; overflow: hidden; visibility: hidden;";
  document.body.appendChild(holder);
  return { container: holder, teardown: () => holder.remove() };
};

// Safety cap on the invisible fingerprint step so a silent ACS never hangs the
// checkout. The backend two-hop loop remains the source of truth for the outcome.
const ADYEN_FINGERPRINT_TIMEOUT_MS = 20_000;

/**
 * Drive a native Adyen 3DS2 action (device fingerprint or ACS challenge) with the
 * Adyen Web SDK. `createFromAction` runs the EMVCo handshake and yields the result
 * as `state.data` (`{ details, paymentData }`) via `onAdditionalDetails`, which the
 * card loop forwards to `/card/verify`. `subtype === "challenge"` renders a visible
 * modal; `fingerprint` (or absent) runs invisibly.
 *
 * The Adyen Web core needs a `clientKey` + `environment`. The backend stamps both onto
 * the opaque `Adyen3dsAction.action` when the connection has native 3DS enabled and a
 * client key configured (`AdyenApi._with_3ds_client_config`, from `AdyenConfig.client_key`
 * / `client_environment` — the analog of `Stripe3dsAction.stripe_pk`), so we read them
 * off the action here. Null-safe: if 3DS is misconfigured (no client key) they are absent
 * and we fail with a clear error instead of initializing the SDK without a key.
 */
const handleAdyenNative3ds = async (action: Record<string, unknown>): Promise<NextActionResult> => {
  const clientKey = typeof action.clientKey === "string" ? action.clientKey : undefined;
  const environment = typeof action.environment === "string" ? action.environment : "test";
  if (!clientKey) {
    console.error("[3DS:Adyen] Native 3DS2 action missing clientKey; cannot initialize Adyen Web.", action.type);
    return {
      success: false,
      error: "Adyen 3DS is not fully configured (missing client key).",
    };
  }

  const isChallenge = action.subtype === "challenge";
  const adyenCheckout = await getLoadedAdyen();

  return new Promise<NextActionResult>((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let teardown: () => void = () => {};
    const settle = (result: NextActionResult): void => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      teardown();
      resolve(result);
    };

    // Challenge → visible modal with a real cancel handler; fingerprint → hidden
    // container plus a safety timeout so a silent ACS can't hang checkout.
    const mounted = isChallenge
      ? mountAdyenChallengeModal(() => settle({ success: false, error: "Verification cancelled" }))
      : mountAdyenHiddenContainer();
    const container = mounted.container;
    const domTeardown = mounted.teardown;
    // Captured from createFromAction() below so teardown can unmount() it. Adyen's
    // component registers a `window` `message` listener (to receive the ACS
    // postMessage); removing the container from the DOM does NOT remove that
    // listener, so without an explicit unmount() it survives across the up-to-5-hop
    // verify loop and leaks a listener per hop.
    let component: AdyenActionComponent | undefined;
    teardown = () => {
      try {
        component?.unmount?.();
      } catch {
        // best-effort — never let unmount block settle/teardown
      }
      domTeardown();
    };
    if (!isChallenge) {
      timeoutId = setTimeout(
        () => settle({ success: false, error: "3DS device verification timed out." }),
        ADYEN_FINGERPRINT_TIMEOUT_MS,
      );
    }

    adyenCheckout({
      clientKey,
      environment,
      onAdditionalDetails: (state) => {
        // The component completed the handshake — round-trip its state.data to the
        // backend verify (the source of truth for the final charge outcome).
        settle({
          success: true,
          data: { details: state.data?.details, paymentData: state.data?.paymentData },
        });
      },
      onError: (error) => {
        console.error("[3DS:Adyen] Native 3DS2 component error:", error);
        settle({ success: false, error: "3DS authentication failed" });
      },
    })
      .then((checkout) => {
        if (settled) return;
        // Capture the component so teardown can unmount() it (see the composed
        // teardown above) — otherwise its `window` `message` listener leaks.
        component = checkout.createFromAction(action, { challengeWindowSize: "05" });
        component.mount(container);
      })
      .catch((error) => {
        console.error("[3DS:Adyen] Failed to initialize Adyen Web:", error);
        settle({ success: false, error: "3DS authentication failed to start" });
      });
  });
};

/**
 * Handle Adyen 3DS. The backend passes Adyen's `action` object through opaquely
 * (`Adyen3dsAction.action`); branch on its polymorphic `type`:
 *  - `threeDS2` → native device fingerprint (invisible) / ACS challenge (modal),
 *    driven by the Adyen Web SDK; produces `state.data` for the verify call.
 *  - `redirect` → shared 3DS iframe (existing behavior).
 */
const handleAdyen3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "adyen_3ds" }>,
): Promise<NextActionResult> => {
  const { action } = nextAction;

  if (action.type === "threeDS2") {
    return handleAdyenNative3ds(action);
  }
  if (action.type === "redirect") {
    return handleAdyenRedirect(action);
  }

  console.error("[3DS:Adyen] Unsupported action type:", action.type);
  return {
    success: false,
    error: `Unsupported Adyen 3DS action type: ${String(action.type)}`,
  };
};

// ---------------------------------------------------------------------------
// RBI India e-mandate consent (PAY-1716 / #4300)
// ---------------------------------------------------------------------------
//
// PKJS renders the mandate consent itself so every embedder gets a compliant
// consent gate for FREE (the DEFAULT modal), exactly as 3DS auto-mounts. This
// hosts OUR OWN terms in a mountAdyenChallengeModal-style overlay — deliberately
// NOT show3DSIframeModal, which is an iframe host for issuer ACS content, not our
// terms.
//
// THE KERNEL (must hold on every surface, modal included):
//   - Never auto-tick the checkbox; never auto-resubmit. The returned promise only
//     resolves `accepted` after the payer ticks the box AND clicks Accept.
//   - Render `consentText` VERBATIM at the accepted version (shown == stored).
//   - The card loop never routes this action through the 3DS auto-resume — that
//     path records NO consent (enforced in card.ts, before handleNextAction).
// Auto-*render* is allowed (3DS auto-mounts too); auto-*accept* is the regression.

/**
 * The mandate-consent next_action, narrowed off the checkout response union by its
 * `stripe_mandate_consent` discriminator.
 */
type MandateConsentAction = Extract<
  NonNullable<PublicCardCheckoutResponse["nextAction"]>,
  { type: "stripe_mandate_consent" }
>;

/**
 * Outcome of the consent surface: `accepted: true` (with the accepted `consentVersion`
 * to forward as evidence) ONLY after tick + Accept; `accepted: false` on cancel.
 */
export type MandateConsentResult = { accepted: true; consentVersion: string } | { accepted: false };

/**
 * PKJS's OWN small copy of the itemized field labels, mirroring `INDIA_MANDATE_FIELD_LABELS`
 * in the customer-portal's `india-mandate.ts`. Deliberately NOT imported from that module (it
 * pulls `@pkg/utils`) and NOT extracted into a shared workspace package — the consent surface
 * stays self-contained per the RBI plan (Finding 1).
 */
const INDIA_MANDATE_FIELD_LABELS = {
  merchant: "Merchant",
  cap: "Maximum per charge",
  frequency: "Frequency",
  start: "Starts",
  validUntil: "Valid until",
} as const;

/** Format the per-charge cap (minor units + ISO currency) for display. */
const formatMandateCap = (capAmountAtom: number, currency: string): string => {
  try {
    // Mirror the portal's mandate-scoped locale rule (india-mandate.ts formatMandateAmount):
    // INR groups Indian lakh/crore (`en-IN`, e.g. ₹1,18,000.00), every other currency keeps
    // the browser default. Kept in step with the portal so the cap can't drift by surface.
    const locale = currency.toUpperCase() === "INR" ? "en-IN" : undefined;
    const fmt = new Intl.NumberFormat(locale, { style: "currency", currency });
    // Divide atoms by the currency's own minor-unit exponent (2 for INR/USD, 0 for JPY).
    const digits = fmt.resolvedOptions().maximumFractionDigits ?? 2;
    return fmt.format(capAmountAtom / 10 ** digits);
  } catch {
    // Unknown/invalid currency code — fall back to a plain 2dp amount + the raw code.
    return `${(capAmountAtom / 100).toFixed(2)} ${currency}`;
  }
};

/** Human-readable debit cadence (mirrors the portal's `buildIndiaMandateCadence`). */
const buildIndiaMandateCadence = (interval: string, intervalCount: number): string => {
  // `sporadic` is the fallback when a recurring checkout's interval was not stamped: there is
  // no fixed schedule, only cap-bounded debits. Kept in step with the portal's copy (DRAFT,
  // pending Taylor sign-off) so the now-live modal surface shows the same wording.
  if (interval === "sporadic") return "Only when a charge is due";
  const unit = intervalCount > 1 ? `${intervalCount} ${interval}s` : interval;
  return `Every ${unit}`;
};

/** Format an ISO-string-or-Date mandate date as a readable calendar date. */
const formatMandateDate = (value: string | Date): string => {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(d);
};

// ---------------------------------------------------------------------------
// Mandate-consent theming
// ---------------------------------------------------------------------------
//
// The consent surface reads the SAME CSS-var theme the hosted checkout uses
// (customer-portal `styles.css` + `checkout-page.tsx`'s `cssVars`, which the portal
// promotes onto `:root` so a body-mounted overlay inherits them). On the hosted
// checkout the Accept button + accents therefore match the merchant's brand exactly,
// the way the checkout's own pay CTA does (`--pk-brand` bg + `--pk-brand-contrast`
// label). EVERY var carries a neutral fallback, so a plain PKJS embedder page (no
// `--pk-*` vars present) keeps the clean neutral look unchanged.
//
// A11y: only surfaces that pair a themed BACKGROUND with a matching foreground consume
// the brand — the Accept button (brand bg + its WCAG-contrast label) and the checkbox
// tint. Body/primary text, the close ✕, and the focus ring stay a dark neutral (`fg`)
// on the white card, so a light merchant brand can never render the consent text, the
// close control, or the focus ring unreadable. (The checkout's own white form area uses
// plain dark text too; `--pk-text`/`--pk-bg` are its DARK-panel colors and would fail
// contrast on this white card, so the modal deliberately does NOT consume them.)
const MANDATE_ACCENT = "var(--pk-brand, #1c1c1c)"; // Accept button bg + checkbox tint
const MANDATE_ACCENT_TEXT = "var(--pk-brand-contrast, #ffffff)"; // Accept button label (contrast-safe)
const MANDATE_SURFACE = "var(--pk-modal-surface, #ffffff)"; // modal card background
const MANDATE_FG = "var(--pk-modal-fg, #1c1c1c)"; // primary/strong text + close glyph + focus ring
const MANDATE_FG_MUTED = "var(--pk-modal-fg-muted, #46597a)"; // secondary text
const MANDATE_BORDER = "var(--pk-modal-border, #e2e6ee)"; // card hairline / dividers
const MANDATE_HOVER = "var(--pk-modal-hover, #f1f3f7)"; // close-button hover tint

/**
 * Optional per-embedder color overrides for the consent surface (PKJS nice-to-have). Each key
 * maps to one of the CSS vars above; set on the modal/inline root so they win over both the
 * neutral fallbacks and any inherited hosted-checkout theme. Reuses the checkout's own
 * `--pk-brand` / `--pk-brand-contrast` names, so this is the SAME theming interface — an
 * embedder that already themes those vars gets a matching modal for free.
 */
export type MandateConsentAppearance = {
  /** Accept-button background + accent → `--pk-brand`. */
  brand?: string;
  /** Accept-button label (keep it readable on `brand`) → `--pk-brand-contrast`. */
  brandText?: string;
  /** Primary/strong text + close glyph + focus ring → `--pk-modal-fg`. */
  text?: string;
  /** Secondary text → `--pk-modal-fg-muted`. */
  mutedText?: string;
  /** Card hairline / dividers → `--pk-modal-border`. */
  border?: string;
  /** Modal card background → `--pk-modal-surface`. */
  surface?: string;
};

/** Apply an embedder `appearance` as CSS vars on the surface root (no-op when absent). */
const applyMandateAppearance = (el: HTMLElement, appearance?: MandateConsentAppearance): void => {
  if (!appearance) return;
  const map: Array<[keyof MandateConsentAppearance, string]> = [
    ["brand", "--pk-brand"],
    ["brandText", "--pk-brand-contrast"],
    ["text", "--pk-modal-fg"],
    ["mutedText", "--pk-modal-fg-muted"],
    ["border", "--pk-modal-border"],
    ["surface", "--pk-modal-surface"],
  ];
  for (const [key, cssVar] of map) {
    const value = appearance[key];
    if (value) el.style.setProperty(cssVar, value);
  }
};

/**
 * Build the consent DOM (shared by the modal and the inline surface): the itemized terms,
 * the VERBATIM consent sentence, an UNCHECKED checkbox, and an Accept button DISABLED until
 * the box is ticked. Returns the handles the surfaces wire up.
 */
const buildMandateConsentContent = (
  action: MandateConsentAction,
): { root: HTMLElement; checkbox: HTMLInputElement; acceptButton: HTMLButtonElement } => {
  const info = action.mandateInfo;

  const root = document.createElement("div");
  root.className = "pk-india-mandate-consent";
  root.style.cssText = `font-family: inherit; color: ${MANDATE_FG}; text-align: left;`;

  const heading = document.createElement("p");
  heading.textContent = "Set up automatic payments";
  heading.style.cssText = `margin: 0 0 12px; font-weight: 600; font-size: 16px; color: ${MANDATE_FG};`;
  root.appendChild(heading);

  // Itemized terms (cap / frequency / start / valid-until), mirroring the portal's <dl>.
  const dl = document.createElement("dl");
  dl.style.cssText = `margin: 0 0 12px; display: flex; flex-direction: column; gap: 4px; font-size: 14px; color: ${MANDATE_FG_MUTED};`;
  // The merchant is already named in the stored consent sentence; this row just makes the same
  // identity scannable in the summary (mirrors the portal's getIndiaMandateFields). Omitted if
  // the wire omits the name.
  const merchantName = info.merchantName?.trim();
  const rows: Array<{ label: string; value: string }> = [
    ...(merchantName ? [{ label: INDIA_MANDATE_FIELD_LABELS.merchant, value: merchantName }] : []),
    { label: INDIA_MANDATE_FIELD_LABELS.cap, value: formatMandateCap(info.capAmountAtom, info.currency) },
    { label: INDIA_MANDATE_FIELD_LABELS.frequency, value: buildIndiaMandateCadence(info.interval, info.intervalCount) },
    { label: INDIA_MANDATE_FIELD_LABELS.start, value: formatMandateDate(info.startDate) },
    { label: INDIA_MANDATE_FIELD_LABELS.validUntil, value: formatMandateDate(info.validityEndDate) },
  ];
  for (const row of rows) {
    const line = document.createElement("div");
    line.style.cssText = "display: flex; justify-content: space-between; gap: 12px;";
    const dt = document.createElement("dt");
    dt.textContent = row.label;
    dt.style.cssText = "flex-shrink: 0;";
    const dd = document.createElement("dd");
    dd.textContent = row.value;
    dd.style.cssText = `margin: 0; text-align: right; font-weight: 600; color: ${MANDATE_FG};`;
    line.appendChild(dt);
    line.appendChild(dd);
    dl.appendChild(line);
  }
  root.appendChild(dl);

  // Informational pre-debit notice (Finding 2): the >=24h-before-each-charge + per-charge-cancel
  // line. Display-only context — it is NOT part of the stored consent sentence (no version bump),
  // mirroring the portal's inline block. textContent (never innerHTML) for safe literal rendering.
  const preDebit = info.preDebitNotice?.trim();
  if (preDebit) {
    const pp = document.createElement("p");
    pp.textContent = preDebit;
    pp.style.cssText = `margin: 0 0 12px; font-size: 12px; color: ${MANDATE_FG_MUTED};`;
    root.appendChild(pp);
  }

  // NOTE: the withdrawal / cancel-anytime wording is deliberately NOT rendered as its own line.
  // It is already the closing clause of the binding consent sentence below (rendered verbatim
  // from `action.consentText`), so a separate line would duplicate it — it appears exactly once.

  // Consent row: an UNCHECKED checkbox whose accessible name is the VERBATIM consent
  // sentence (shown == stored). textContent (never innerHTML) so the server text is
  // rendered literally and safely.
  const label = document.createElement("label");
  label.style.cssText = "display: flex; align-items: flex-start; gap: 12px; cursor: pointer;";

  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.checked = false; // NEVER auto-ticked — the kernel
  checkbox.className = "pk-india-mandate-consent-checkbox";
  // accent-color tints the ticked box to the merchant brand on hosted checkout (neutral dark
  // on a plain embedder); the box is supplementary — the label sentence carries the meaning.
  checkbox.style.cssText = `margin-top: 2px; width: 18px; height: 18px; flex-shrink: 0; cursor: pointer; accent-color: ${MANDATE_ACCENT};`;

  const sentence = document.createElement("span");
  sentence.textContent = action.consentText; // VERBATIM — shown == stored
  sentence.id = `pk-india-mandate-consent-text-${Math.random().toString(36).slice(2)}`;
  sentence.style.cssText = `flex: 1; font-size: 14px; color: ${MANDATE_FG_MUTED};`;
  // Accessible name for the checkbox comes from the visible consent sentence.
  checkbox.setAttribute("aria-labelledby", sentence.id);

  label.appendChild(checkbox);
  label.appendChild(sentence);
  root.appendChild(label);

  const acceptButton = document.createElement("button");
  acceptButton.type = "button";
  acceptButton.textContent = "Accept";
  acceptButton.disabled = true; // DISABLED until the box is ticked — the kernel
  acceptButton.className = "pk-india-mandate-consent-accept";
  // Mirrors the hosted checkout's primary pay CTA: merchant-brand background with its
  // WCAG-contrast label (order-summary.tsx uses the same --pk-brand / --pk-brand-contrast pair).
  acceptButton.style.cssText =
    "margin-top: 16px; width: 100%; padding: 10px 16px; border: none; border-radius: 6px;" +
    ` background: ${MANDATE_ACCENT}; color: ${MANDATE_ACCENT_TEXT}; font-size: 14px; font-weight: 600; cursor: pointer;`;

  const syncAccept = (): void => {
    acceptButton.disabled = !checkbox.checked;
    acceptButton.style.opacity = checkbox.checked ? "1" : "0.5";
    acceptButton.style.cursor = checkbox.checked ? "pointer" : "not-allowed";
  };
  syncAccept();
  checkbox.addEventListener("change", syncAccept);

  root.appendChild(acceptButton);

  // Visible keyboard-focus ring on the interactive controls (inline styles can't express
  // :focus-visible, so inject a scoped rule for the checkbox, the Accept button, and the modal's
  // close button) plus a hover cue on the close button so it reads clearly as a control.
  const style = document.createElement("style");
  // Focus ring stays a dark neutral (fg), never the brand, so it is always visible on the
  // white card even under a pale merchant brand (a11y).
  style.textContent =
    ".pk-india-mandate-consent-checkbox:focus-visible," +
    " .pk-india-mandate-consent-accept:focus-visible," +
    " .pk-india-mandate-consent-close:focus-visible" +
    ` { outline: 2px solid ${MANDATE_FG}; outline-offset: 2px; }` +
    ` .pk-india-mandate-consent-close:hover { background: ${MANDATE_HOVER}; }`;
  root.appendChild(style);

  return { root, checkbox, acceptButton };
};

/**
 * DEFAULT surface — auto-mount the consent as a dialog overlay (mirroring the
 * `mountAdyenChallengeModal` chrome, the same way that one mirrors show3DSIframeModal).
 * Resolves `accepted` only on tick + Accept;
 * close button / Escape / scrim resolve to a cancellation.
 */
const mountMandateConsentModal = (
  action: MandateConsentAction,
  appearance?: MandateConsentAppearance,
): Promise<MandateConsentResult> => {
  return new Promise((resolve) => {
    let settled = false;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const overlay = document.createElement("div");
    overlay.id = "pk-india-mandate-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Set up automatic payments");
    overlay.style.cssText = `
      position: fixed; inset: 0; width: 100%; height: 100%;
      background: ${MODAL_OVERLAY_SCRIM};
      display: flex; align-items: center; justify-content: center; z-index: 10000;`;

    const container = document.createElement("div");
    container.style.cssText = `
      position: relative; background: ${MANDATE_SURFACE}; border: 1px solid ${MANDATE_BORDER};
      border-radius: 6px; padding: 24px;
      width: 480px; max-width: calc(100vw - 32px); max-height: calc(100vh - 64px); overflow: auto;
      box-shadow: ${MODAL_SHADOW};`;
    // Embedder color overrides: set on the container so the card, its contents (root), and the
    // close button all inherit them (nice-to-have — no-op when not supplied / on hosted checkout).
    applyMandateAppearance(container, appearance);

    // A clean, clearly-visible close control: the ✕ GLYPH itself carries the visibility — a
    // large (18px) heavy (stroke 2.5) dark stroke (currentColor #1c1c1c) — so it reads on its
    // own with NO chip/border/background/shadow at rest. A generous 36px hit-area (padded well
    // past the ~14px glyph) pinned top-right, only a subtle background tint on :hover plus the
    // injected :focus-visible ring for a11y, and an explicit "Close" accessible name.
    const closeButton = document.createElement("button");
    closeButton.setAttribute("aria-label", "Close");
    closeButton.setAttribute("type", "button");
    closeButton.className = "pk-india-mandate-consent-close";
    closeButton.style.cssText = `
      position: absolute; top: 12px; right: 12px; width: 36px; height: 36px; padding: 0;
      display: flex; align-items: center; justify-content: center;
      border: none; background: transparent; color: ${MANDATE_FG}; border-radius: 8px;
      cursor: pointer; z-index: 10;`;
    closeButton.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" ' +
      'xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M2 2L16 16M2 16L16 2" stroke="currentColor" stroke-width="2.5" ' +
      'stroke-linecap="round"/></svg>';

    const { root, checkbox, acceptButton } = buildMandateConsentContent(action);

    container.appendChild(closeButton);
    container.appendChild(root);
    overlay.appendChild(container);
    document.body.appendChild(overlay);

    const settle = (result: MandateConsentResult): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener("keydown", handleKeydown);
      overlay.remove();
      try {
        previouslyFocused?.focus?.();
      } catch {
        // best-effort focus restore
      }
      resolve(result);
    };

    const cancel = (): void => settle({ accepted: false });

    closeButton.addEventListener("click", cancel);
    // Scrim click (mousedown on the overlay itself, not its children) cancels.
    overlay.addEventListener("mousedown", (event) => {
      if (event.target === overlay) cancel();
    });
    acceptButton.addEventListener("click", () => {
      // Defense in depth: never resolve accepted without a tick, even if the button
      // is somehow clicked while disabled.
      if (!checkbox.checked) return;
      settle({ accepted: true, consentVersion: action.consentVersion });
    });

    // Focus trap (mirrors show3DSIframeModal): Escape cancels; Tab cycles within the
    // dialog. The Accept button only joins the cycle once enabled.
    const handleKeydown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        cancel();
        return;
      }
      if (event.key !== "Tab") return;
      event.preventDefault();
      const focusable: HTMLElement[] = [closeButton, checkbox];
      if (!acceptButton.disabled) focusable.push(acceptButton);
      const idx = focusable.indexOf(document.activeElement as HTMLElement);
      const dir = event.shiftKey ? -1 : 1;
      const next = focusable[(idx + dir + focusable.length) % focusable.length] ?? focusable[0];
      next?.focus();
    };
    window.addEventListener("keydown", handleKeydown);

    // Move focus INTO the dialog on open (the checkbox — the primary control).
    checkbox.focus();
  });
};

/**
 * NON-DEFAULT surface — render the SAME consent content inline into a caller-supplied
 * container (for a merchant that wants it in its own page flow). Resolves `accepted`
 * only on tick + Accept. There is no cancel affordance — the merchant owns the page flow.
 */
const mountMandateConsentInline = (
  action: MandateConsentAction,
  target: HTMLElement,
  appearance?: MandateConsentAppearance,
): Promise<MandateConsentResult> => {
  return new Promise((resolve) => {
    let settled = false;
    const { root, checkbox, acceptButton } = buildMandateConsentContent(action);
    // Embedder color overrides live on the root the merchant hosts (no-op when not supplied).
    applyMandateAppearance(root, appearance);
    target.appendChild(root);
    acceptButton.addEventListener("click", () => {
      if (!checkbox.checked) return; // never resolve accepted without a tick
      if (settled) return;
      settled = true;
      root.remove();
      resolve({ accepted: true, consentVersion: action.consentVersion });
    });
  });
};

/**
 * Render the RBI e-mandate consent surface and resolve with the payer's decision.
 *
 * DEFAULT is the auto-mounted modal. Passing `inlineContainer` renders the same content
 * inline into that element (falling back to the modal if the container can't be resolved,
 * so the payer is never left without a consent surface). Optional `appearance` sets the
 * surface's theme CSS vars for a plain embedder that wants to color-match its own page.
 */
export const handleMandateConsent = (
  action: MandateConsentAction,
  options?: { inlineContainer?: HTMLElement | string; appearance?: MandateConsentAppearance },
): Promise<MandateConsentResult> => {
  const inline = options?.inlineContainer;
  const appearance = options?.appearance;
  if (inline) {
    const target = typeof inline === "string" ? document.querySelector<HTMLElement>(inline) : inline;
    if (target) {
      return mountMandateConsentInline(action, target, appearance);
    }
    console.warn("[PaymentKit] India e-mandate inline container not found; falling back to the modal.");
  }
  return mountMandateConsentModal(action, appearance);
};

/**
 * Handle a next action based on its type.
 * Routes to the appropriate processor-specific handler.
 *
 * @param nextAction - The next action from the checkout response
 * @returns Result indicating success or failure with error message
 */
export const handleNextAction = async (
  nextAction: NonNullable<PublicCardCheckoutResponse["nextAction"]>,
): Promise<NextActionResult> => {
  switch (nextAction.type) {
    case "stripe_3ds":
      return handleStripe3ds(nextAction);
    case "stripe_setup_3ds":
      return handleStripeSetup3ds(nextAction);
    case "airwallex_3ds":
      return handleAirwallex3ds(nextAction);
    case "checkoutcom_3ds":
      return handleCheckoutcom3ds(nextAction);
    case "vonpay_3ds":
      return handleVonpay3ds(nextAction);
    case "vgs_3ds":
      return handleVgs3ds(nextAction);
    case "adyen_3ds":
      return handleAdyen3ds(nextAction);
    case "braintree_3ds":
      return handleBraintree3ds(nextAction);
    default:
      // TypeScript will catch if we miss a case when new action types are added
      return { success: false, error: `Unknown next action type: ${(nextAction as { type: string }).type}` };
  }
};
